import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import UsersOutlineIcon from '../../common/icons/files/UsersOutlineIcon';
import ProfileCircle from '../../common/profile/ProfileCircle';
import { getIdMap } from '../../helpers/helpers';
import { getTextColor } from '../scheduleHelpers';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';
import BuildingIcon from './BuildingIcon';
import LayersIcon from './LayersIcon';

const compactTime = (millis) => {
  if (!millis) return '';
  return DateTime.fromMillis(millis).toLocaleString(DateTime.TIME_SIMPLE).replace(':00', '');
};

const formatDuration = (startTime, endTime) => {
  const minutes = Math.max(0, Math.round((endTime - startTime) / 60000));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  if (mins) return `${mins}m`;
  return '';
};

const headerTextColor = (color = '') => {
  const hex = color.replace('#', '');
  if (hex.length < 6) return '#fff';
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some((channel) => Number.isNaN(channel))) return '#fff';
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  return luminance > 180 ? 'lab(8.20799 -2.04457 -13.5596)' : '#fff';
};

function initialsFor(name = '') {
  const [first = ' ', last = ' '] = name.split(' ').filter((word) => !!word?.length);
  return `${first[0]}${last[0]}`;
}

export default function HoverCardContent({
  title,
  color,
  startTime,
  endTime,
  allDay,
  users,
  projectId,
  categoryName,
  categoryColor,
  isEvent,
  isDraft,
  phaseId,
  costcodeId,
}) {
  const phases = useSelector((state) => state.costcodes.phases);
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const projects = useSelector((state) => state.projects.projects);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const phaseMap = useMemo(() => getIdMap(phases), [phases]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const project = projectMap[projectId];
  const projectName = project
    ? formatProjectLabelFromCompanySettings({
      name: project.name,
      number: project.number,
      settings,
    })
    : '';
  const phaseName = phaseMap[phaseId]?.name;
  const costcode = costcodeIdMap?.[costcodeId];
  const costcodeLabel = costcode
    ? [costcode.code, costcode.name].filter(Boolean).join(' ')
    : '';
  const phaseCostLabel = [phaseName, costcodeLabel].filter(Boolean).join(' · ');
  const stopGridMouse = (event) => {
    event.stopPropagation();
  };

  const startLabel = allDay ? 'All day' : compactTime(startTime);
  const endLabel = allDay ? '' : compactTime(endTime);
  const timeLabel = endLabel ? `${startLabel}–${endLabel}` : startLabel;
  const duration = allDay ? '' : formatDuration(startTime, endTime);
  const assigned = users.filter((user) => user.name);

  return (
    <div // eslint-disable-line jsx-a11y/no-static-element-interactions
      className="schedule-shift-hover-card"
      onMouseDown={stopGridMouse}
      onMouseUp={stopGridMouse}
    >
      <div
        className="schedule-shift-hover-card-header"
        style={{ backgroundColor: color, color: headerTextColor(color) }}
      >
        <div className="schedule-shift-hover-card-title">{title || timeLabel}</div>
        <div className="schedule-shift-hover-card-type-group">
          <div className="schedule-shift-hover-card-type">{isEvent ? 'Event' : 'Shift'}</div>
          {!!isDraft && (
            <div className="schedule-shift-hover-card-type">Draft</div>
          )}
        </div>
      </div>
      <div className="schedule-shift-hover-card-body">
        <div className="schedule-shift-hover-card-times">
          <span>{timeLabel}</span>
          {!!duration && <span className="schedule-shift-hover-card-duration">{duration}</span>}
        </div>
        {assigned.length > 0 && (
          <div className="schedule-shift-hover-card-section">
            <div className="schedule-shift-hover-card-label schedule-shift-hover-card-assigned">
              <span className="schedule-shift-hover-card-assigned-text">
                <UsersOutlineIcon width={14} height={14} />
                {`${assigned.length} assigned`}
              </span>
              <div className="schedule-shift-hover-card-avatars">
                {assigned.map((user, idx) => (
                  <ProfileCircle
                    key={user.id}
                    radius={8}
                    fontSize={8}
                    initials={initialsFor(user.name)}
                    className="schedule-item-user-circle"
                    style={{
                      backgroundColor: user.scheduleColor,
                      zIndex: assigned.length - idx,
                    }}
                    accentColor={user.scheduleColor ? getTextColor(user.scheduleColor) : undefined}
                  />
                ))}
              </div>
            </div>
            {assigned.map((user) => (
              <div className="schedule-shift-hover-card-person" key={user.id}>
                <span
                  className="schedule-shift-hover-card-dot"
                  style={{ backgroundColor: user.scheduleColor || '#A1A1AA' }}
                />
                {user.name}
              </div>
            ))}
          </div>
        )}
        {!isEvent && !!projectName && (
          <div className="schedule-shift-hover-card-label">
            <BuildingIcon />
            {projectName}
          </div>
        )}
        {!!phaseCostLabel && (
          <div className="schedule-shift-hover-card-label schedule-shift-hover-card-phase">
            <LayersIcon />
            <span>{phaseCostLabel}</span>
          </div>
        )}
        {!!categoryName && (
          <div className="schedule-shift-hover-card-pill">
            <span
              className="schedule-shift-hover-card-dot"
              style={{ backgroundColor: categoryColor || '#A1A1AA' }}
            />
            {categoryName}
          </div>
        )}
      </div>
      <div className="schedule-shift-hover-card-footer">
        {isEvent ? 'Click to open event details' : 'Click to open shift details'}
      </div>
    </div>
  );
}

HoverCardContent.propTypes = {
  title: PropTypes.string,
  color: PropTypes.string,
  startTime: PropTypes.number,
  endTime: PropTypes.number,
  allDay: PropTypes.bool,
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    scheduleColor: PropTypes.string,
  })),
  projectId: PropTypes.string,
  categoryName: PropTypes.string,
  categoryColor: PropTypes.string,
  isEvent: PropTypes.bool,
  isDraft: PropTypes.bool,
  phaseId: PropTypes.string,
  costcodeId: PropTypes.string,
};

HoverCardContent.defaultProps = {
  title: '',
  color: '',
  startTime: null,
  endTime: null,
  allDay: false,
  users: [],
  projectId: null,
  categoryName: null,
  categoryColor: null,
  isEvent: false,
  isDraft: false,
  phaseId: null,
  costcodeId: null,
};
